<div class="home" *ngIf="!loading">
  <!-- Hero -->
  <div class="hero">
    <h1>{{ greeting }}</h1>
    <div class="hero-sub">{{ subtitle }}</div>
    <div class="streak-badge" *ngIf="showStreakBadge">
      <span class="flame"></span>{{ 'shell.streakBadge' | translate:{ n: streak } }}
    </div>
  </div>

  <!-- Upgrade upsell + first-results (unpaid user, after first scan) -->
  <ng-container *ngIf="showUpsell">
    <div class="upsell">
      <div class="upsell-title">{{ 'shell.home.upsell.title' | translate }}</div>
      <div class="upsell-body">{{ upsellBodyKey | translate }}</div>
      <button class="upsell-cta" (click)="openPaywall()">{{ 'shell.home.upsell.cta' | translate }}</button>
    </div>
    <button class="first-results" (click)="viewFirstResults()" *ngIf="firstScanGuid">
      <div class="fr-text">
        <div class="fr-title">{{ 'shell.home.firstResults.title' | translate }}</div>
        <div class="fr-sub">{{ 'shell.home.firstResults.sub' | translate }}</div>
      </div>
      <svg class="fr-chev" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--brand-color)" stroke-width="1.8"><polyline points="9 18 15 12 9 6" /></svg>
    </button>
  </ng-container>

  <!-- Today's routine + progress flower (removed in Option A — usage zones are
       meaningless while tools are locked, DEV_SPEC §109; kept in Option B) -->
  <ng-container *ngIf="hasPlan; else noPlan">
   <ng-container *ngIf="!toolsLocked">
    <!-- (a) What is left for today is the first thing on the page: nothing sits
         above it but the greeting. -->
    <!-- Danna 26.09: a real way to take tools OUT of her routine from home.
         "Edit" turns the day into her whole routine with a remove toggle under
         every tool. A removed tool stays in place, greyed, and a second tap
         puts it back; it leaves the page only when she taps Done. -->
    <div class="zone" *ngIf="editing">
      <div class="zone-header-row">
        <div class="zone-header">{{ 'shell.home.edit.title' | translate }}</div>
        <button type="button" class="zone-edit done" [disabled]="editBusy.size > 0" (click)="finishEdit()">
          {{ 'shell.home.edit.done' | translate }}
        </button>
      </div>
      <div class="edit-hint">{{ 'shell.home.edit.hint' | translate }}</div>
      <div class="today-cards">
        <app-tool-card
          *ngFor="let c of editCards"
          [toolId]="c.id"
          [name]="c.name"
          [removable]="true"
          [removed]="editRemoved.has(c.id)"
          [busy]="editBusy.has(c.id)"
          (pick)="toggleRemove($event)"
        ></app-tool-card>
      </div>
    </div>
    <ng-container *ngIf="!editing">
    <div class="zone" *ngIf="remaining.length > 0; else allDoneTpl">
      <div class="zone-header-row">
        <div class="zone-header">{{ 'shell.home.todayHeader' | translate }}</div>
        <button type="button" class="zone-edit" (click)="startEdit()">{{ 'shell.home.edit.open' | translate }}</button>
      </div>
      <!-- B12: ONE dominant card for the next action - the next tool in her plan
           she has not done yet, with its short subtitle and how long it takes. -->
      <button class="next-card" *ngIf="nextCard as n" (click)="openTool(n.id)">
        <div class="next-card-icon"><img [src]="img(n.id)" alt="" loading="lazy" /></div>
        <div class="next-card-body">
          <span class="next-card-eyebrow">{{ 'shell.home.nextUp' | translate }}</span>
          <span class="next-card-name">{{ n.name }}</span>
          <span class="next-card-desc" *ngIf="n.desc">{{ n.desc }}</span>
        </div>
        <span class="next-card-mins" *ngIf="n.mins">{{ 'shell.home.minutes' | translate:{ n: n.mins } }}</span>
      </button>
      <!-- (3) the tool shown in the card above is NOT repeated here, and (c) the
           body metrics ride along as ordinary task cards instead of a section of
           their own. (d) each one carries a "to do" tag so what is still waiting
           reads apart from the recommendations further down. -->
      <div class="today-cards">
        <app-tool-card
          *ngFor="let c of restCards"
          [toolId]="c.id"
          [name]="c.name"
          [mins]="c.mins"
          [status]="cardStatus(c.done)"
          [locked]="toolsLocked"
          (pick)="openTool($event)"
        ></app-tool-card>
      </div>
    </div>
    <ng-template #allDoneTpl>
      <div class="congrats">
        <div class="congrats-title">{{ 'shell.home.allDoneTitle' | translate }}</div>
        <div class="congrats-body">{{ 'shell.home.allDoneBody' | translate }}</div>
        <button type="button" class="zone-edit" (click)="startEdit()">{{ 'shell.home.edit.open' | translate }}</button>
      </div>
    </ng-template>
    </ng-container>

    <!-- (b) the three counters are one quiet row that says what it counts: the
         day, the month, and the same bar. The bloom shrinks to its icon. -->
    <div class="count-row">
      <canvas #flowerCanvas class="count-flower" width="400" height="480" aria-hidden="true"></canvas>
      <!-- Danna 22.09 (copy round): before she has done anything the row said
           "0 of 3", which is a score, not a start. The empty state says so in
           words and the second line names what is waiting instead. -->
      <div class="count-text">
        <div class="count-today">
          {{ (doneCount === 0 ? 'shell.home.counters.todayEmpty' : 'shell.home.counters.today') | translate:{ done: doneCount, total: planTotal } }}
        </div>
        <div class="count-month">
          {{ doneCount === 0
              ? ('shell.home.counters.totalTools' | translate:{ total: planTotal })
              : ('shell.home.counters.month' | translate:{ n: engActive, target: engTarget }) }}
        </div>
      </div>
      <div class="count-bar">
        <div class="eng-bar">
          <div class="eng-fill" [class.reached]="engReached" [style.width.%]="engPct"></div>
        </div>
      </div>
    </div>
   </ng-container>
  </ng-container>

  <ng-template #noPlan>
    <div class="no-plan">
      <div class="np-emoji" aria-hidden="true">🌱</div>
      <div class="np-title">{{ 'shell.home.noPlanTitle' | translate }}</div>
      <div class="np-body">{{ 'shell.home.noPlanBody' | translate }}</div>
    </div>
  </ng-template>

  <!-- More tools. (e) every tile says what it is, three to a row so the subtitle
       has somewhere to live. -->
  <div class="more" *ngIf="moreTools.length">
    <div class="more-header">{{ 'shell.home.moreTools' | translate }}</div>
    <div class="more-grid">
      <app-tool-card
        *ngFor="let t of moreTools"
        [toolId]="t.id"
        [name]="t.name"
        [desc]="t.desc"
        [why]="t.why"
        [selected]="selectedMoreTools.has(t.id)"
        [locked]="toolsLocked"
        [dismissable]="!toolsLocked"
        [dismissed]="isDismissed(t.id)"
        (pick)="toolsLocked ? openPaywall() : toggleToolSelection(t.id)"
        (dismiss)="dismissTool($event)"
        (undo)="undoDismissTool($event)"
      ></app-tool-card>
    </div>
    <button class="more-add" *ngIf="selectedMoreTools.size" [disabled]="isAddingTools" (click)="addSelectedTools()">
      {{ (isAddingTools ? 'results.tools.rec.adding' : 'results.tools.rec.cta') | translate }}
    </button>
  </div>

  <!-- (f) "Worth knowing" is something to read, so it sits after the actions.
       (removed in Option A — meaningless while tools are locked, DEV_SPEC §109) -->
  <div class="insight-card" *ngIf="!toolsLocked">
    <div class="insight-icon">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--brand-color)" stroke-width="2" stroke-linecap="round">
        <path d="M9 21h6" /><path d="M12 17v-1" />
        <path d="M12 2C8 2 5 5 5 9c0 2.5 1.2 4.5 3 5.7V16h8v-1.3c1.8-1.2 3-3.3 3-5.7 0-4-3-7-7-7z" />
      </svg>
    </div>
    <div class="insight-text">
      <strong>{{ 'shell.insightTitle' | translate }}</strong><br />{{ insight }}
    </div>
  </div>
  <div class="insight-disc" *ngIf="!toolsLocked">{{ 'shell.insightDisclaimer' | translate }}</div>

  <!-- MARKABLE assessment link -->
  <button class="assess" (click)="startAssessment()">
    <div class="assess-icon" [class.is-nudging]="scanNudge">
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--brand-color)" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
        <path d="M4 9V7a3 3 0 0 1 3-3h2" /><path d="M15 4h2a3 3 0 0 1 3 3v2" /><path d="M20 15v2a3 3 0 0 1-3 3h-2" /><path d="M9 20H7a3 3 0 0 1-3-3v-2" />
        <g class="assess-face"><path d="M9 10h.01" /><path d="M15 10h.01" /><path d="M9.2 14a3.6 3.6 0 0 0 5.6 0" /></g>
      </svg>
    </div>
    <div class="assess-text">
      <div class="assess-title">{{ 'shell.assessment.title' | translate }}</div>
      <div class="assess-sub">{{ 'shell.assessment.sub' | translate }}</div>
      <div class="assess-next" *ngIf="nextScanDays !== null">
        {{ nextScanDays === 1 ? ('shell.home.nextScanTomorrow' | translate) : ('shell.home.nextScan' | translate: { days: nextScanDays }) }}
      </div>
    </div>
    <svg class="assess-chev" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--brand-color)" stroke-width="1.8"><polyline points="9 18 15 12 9 6" /></svg>
  </button>
  <!-- New-scan clarity (unpaid): the scan is free, full results open separately -->
  <div class="newscan-note" *ngIf="showUpsell">{{ 'shell.home.newScanNote' | translate }}</div>

  <!-- §4: view previous results without redoing the scan -->
  <button class="assess-prev" *ngIf="prevScanGuid" (click)="viewPrevious()">
    {{ 'shell.home.viewPrevious' | translate }}
  </button>

  <!-- "Keep tracking from your phone". Only once she has a report to open there,
       because that is what the card promises. The component itself is already
       Markable-only and hidden inside the installed app. -->
  <app-download-card *ngIf="prevScanGuid" variant="card" placement="home"></app-download-card>

  <div class="home-disc">{{ 'shell.disclaimer.home' | translate }}</div>
</div>
